// paymentRunButton.js - Adds a button to the payment run execute page.
// The overview table stores bank-account summaries (itemIds only). The order
// records live deeper in React state. A text search for "executionDate" hits
// the i18n catalog first, so this walk matches order objects by their fields
// and keeps the ones referenced by the visible summaries.
//
// executionDate and counterpartIban are optional on the order. Requiring both
// drops the whole download as soon as one order is missing either field.

let Lux_PaymentRun_observer = null

const Lux_PAYMENT_STATES = {
  NEW: true,
  SENT_TO_BANK: true,
  DOWNLOADED: true,
  APPROVED: true,
  FAILED: true,
  DELETED: true,
  DUPLICATE: true,
  DISMISSED: true,
}

function Lux_isId(value) {
  return typeof value === 'number' || typeof value === 'string'
}

function Lux_isPaymentOrder(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  if (!Lux_isId(value.id) || Array.isArray(value.itemIds)) return false
  if (typeof value.paymentState === 'string' && Lux_PAYMENT_STATES[value.paymentState]) return true
  const amountOk = typeof value.amount === 'number' || typeof value.amount === 'string'
  if (!amountOk) return false
  return (
    typeof value.counterpartName === 'string' ||
    typeof value.counterpartIban === 'string' ||
    typeof value.propertyIban === 'string'
  )
}

function Lux_isBankAccountSummary(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray(value.itemIds) &&
    (typeof value.accountHolderName === 'string' || value.numberOrders != null) &&
    (typeof value.iban === 'string' || value.bankAccountId != null)
  )
}

function Lux_parseAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const cleaned = value.replace(/\s/g, '').replace(/€/g, '')
  if (!cleaned) return null
  const normalized = cleaned.includes(',')
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : cleaned
  const amount = Number(normalized)
  return Number.isFinite(amount) ? amount : null
}

function Lux_orderAmount(order) {
  if (typeof order.unformattedAmount === 'number' && Number.isFinite(order.unformattedAmount)) {
    return order.unformattedAmount
  }
  const parsed = Lux_parseAmount(order.amount)
  return parsed == null ? 0 : parsed
}

// The screen keeps two copies of an order. The API copy has a numeric amount.
// The table copy has German text ("186,75", "08.10.2026"). Prefer the number,
// and never let a later 0 replace an amount we already have.
function Lux_shouldReplaceOrder(existing, order) {
  const existingAmount = Lux_orderAmount(existing)
  const nextAmount = Lux_orderAmount(order)
  const existingIsNumber = typeof existing.amount === 'number' || typeof existing.unformattedAmount === 'number'
  const nextIsNumber = typeof order.amount === 'number' || typeof order.unformattedAmount === 'number'
  if (existingAmount !== 0 && nextAmount === 0) return false
  if (existingAmount === 0 && nextAmount !== 0) return true
  if (nextIsNumber && !existingIsNumber) return true
  if (!nextIsNumber && existingIsNumber) return false
  return true
}

function Lux_rememberOrder(orders, ordersByExchange, order) {
  const key = String(order.id)
  const existing = orders.get(key)
  if (!existing || Lux_shouldReplaceOrder(existing, order)) orders.set(key, order)

  if (!Lux_isId(order.exchangeId)) return
  const exchangeKey = String(order.exchangeId)
  const existingExchange = ordersByExchange.get(exchangeKey)
  if (!existingExchange || Lux_shouldReplaceOrder(existingExchange, order)) {
    ordersByExchange.set(exchangeKey, order)
  }
}

function Lux_orderForItemId(orders, ordersByExchange, id) {
  return orders.get(id) || ordersByExchange.get(id) || null
}

function Lux_calendarDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function Lux_executionDate(value) {
  if (typeof value === 'string') {
    const german = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
    if (german) return `${german[3]}-${german[2]}-${german[1]}`
    return value
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return Lux_calendarDate(value)
  if (value && typeof value.toDate === 'function') {
    try {
      const date = value.toDate()
      if (date instanceof Date && !Number.isNaN(date.getTime())) return Lux_calendarDate(date)
    } catch {
      return ''
    }
  }
  return ''
}

function Lux_paymentOverviewRow(order) {
  return {
    id: order.id,
    propertyIban: order.propertyIban || '',
    propertyAccountInstitute: order.propertyAccountInstitute || '',
    propertyName: order.propertyName || '',
    propertyHrId: order.propertyHrId || '',
    counterpartName: order.counterpartName || '',
    counterpartIban: order.counterpartIban || '',
    amount: Lux_orderAmount(order),
    statement: order.statement || '',
    executionDate: Lux_executionDate(order.executionDate),
    paymentState: order.paymentState || '',
    invoiceHrId: order.invoiceHrId || '',
  }
}

function Lux_reactFiberFromElement(element) {
  const fiberKey = Object.keys(element).find(key =>
    key.startsWith('__reactFiber$') || key.startsWith('__reactInternalInstance$')
  )
  return fiberKey ? element[fiberKey] : null
}

/**
 * Orders for the rows currently shown in #PaymentRunOverview.
 * Returns null until every summary itemId has a matching order object.
 */
function Lux_extractPaymentsFromState() {
  const container = document.querySelector('#PaymentRunOverview')
  if (!container) return null

  const orders = new Map()
  const ordersByExchange = new Map()
  const itemIds = new Set()
  const seen = new WeakSet()
  const globalBudget = { n: 40000 }

  const walk = (value, budget) => {
    if (!value || typeof value !== 'object' || budget.n <= 0 || globalBudget.n <= 0) return
    if (seen.has(value)) return
    seen.add(value)
    budget.n--
    globalBudget.n--

    if (value.nodeType || value.$$typeof || value instanceof Date || value._isAMomentObject) return

    let keys = null
    if (!Array.isArray(value)) {
      try {
        keys = Object.keys(value)
      } catch {
        return
      }
      if (keys.includes('bindI18n') || keys.includes('changeLanguage')) return

      if (Lux_isPaymentOrder(value)) {
        Lux_rememberOrder(orders, ordersByExchange, value)
        return
      }

      if (Lux_isBankAccountSummary(value)) {
        for (const item of value.itemIds) {
          if (Lux_isId(item)) {
            itemIds.add(String(item))
          } else if (Lux_isPaymentOrder(item)) {
            Lux_rememberOrder(orders, ordersByExchange, item)
            itemIds.add(String(item.id))
          }
        }
      }
    }

    let children
    try {
      children = Array.isArray(value) ? value : Object.values(value)
    } catch {
      return
    }
    const ordered = Array.isArray(value)
      ? children
      : children.filter(child => Array.isArray(child)).concat(children.filter(child => !Array.isArray(child)))
    for (const child of ordered) {
      if (child && typeof child === 'object') walk(child, budget)
    }
  }

  const fibers = new WeakSet()
  for (const element of container.querySelectorAll('*')) {
    let fiber = Lux_reactFiberFromElement(element)
    while (fiber) {
      if (fibers.has(fiber)) break
      fibers.add(fiber)

      let hook = fiber.memoizedState
      let hookIndex = 0
      while (hook && hookIndex < 80) {
        walk(hook.memoizedState, { n: 8000 })
        hook = hook.next
        hookIndex++
      }

      fiber = fiber.return
    }
  }

  if (itemIds.size === 0) return null

  const payments = []
  const seenOrders = new Set()
  for (const id of itemIds) {
    const order = Lux_orderForItemId(orders, ordersByExchange, id)
    if (!order) return null
    if (seenOrders.has(order)) continue
    seenOrders.add(order)
    payments.push(Lux_paymentOverviewRow(order))
  }

  return payments
}

/**
 * Set button loading state
 */
function Lux_setPaymentButtonLoading(button, isLoading) {
  if (isLoading) {
    button.disabled = true
    button.dataset.originalText = button.textContent
    button.innerHTML = `
      <span style="display: inline-flex; align-items: center; gap: 6px;">
        <span class="lux-spinner" style="
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(0, 0, 0, 0.2);
          border-top-color: #1890ff;
          border-radius: 50%;
          animation: lux-spin 0.8s linear infinite;
        "></span>
        Wird erstellt...
      </span>
    `
    if (!document.getElementById('lux-spinner-styles')) {
      const style = document.createElement('style')
      style.id = 'lux-spinner-styles'
      style.textContent = `
        @keyframes lux-spin {
          to { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }
  } else {
    button.disabled = false
    button.textContent = button.dataset.originalText || 'Herunterladen'
  }
}

function Lux_initializePaymentRunButton() {
  const anchorSelector = '#PaymentRunOverview > div > div.section-content > div > div.ant-col.ant-col-12'
  const buttonId = 'lux-payment-run-btn'

  // Disconnect previous observer if exists
  if (Lux_PaymentRun_observer) {
    Lux_PaymentRun_observer.disconnect()
    Lux_PaymentRun_observer = null
  }

  // Function to create and inject the button
  const injectButton = (anchorElement) => {
    if (document.getElementById(buttonId)) {
      return
    }

    // Clone the existing column structure
    const clonedCol = anchorElement.cloneNode(true)
    clonedCol.id = buttonId + '-container'

    // Update the label text
    const labelElement = clonedCol.querySelector('.ant-col.ant-form-item-label')
    if (labelElement) {
      const labelText = labelElement.querySelector('label')
      if (labelText) {
        labelText.textContent = 'Zahlungsübersicht herunterladen'
      }
    }

    // Remove the ant-form-item-control and replace with button
    const controlElement = clonedCol.querySelector('.ant-col.ant-form-item-control')
    if (controlElement) {
      // Create the button - initially disabled
      const paymentRunButton = document.createElement('button')
      paymentRunButton.type = 'button'
      paymentRunButton.className = 'ant-btn ant-btn-primary Button'
      paymentRunButton.style.height = '40px'
      paymentRunButton.id = buttonId
      paymentRunButton.textContent = 'Herunterladen'
      paymentRunButton.disabled = true
      paymentRunButton.style.opacity = '0.5'
      paymentRunButton.style.cursor = 'not-allowed'

      // Check for payments periodically and enable button when found
      let paymentCheckInterval = null
      const checkForPayments = () => {
        const payments = Lux_extractPaymentsFromState()
        if (payments && payments.length > 0) {
          paymentRunButton.disabled = false
          paymentRunButton.style.opacity = '1'
          paymentRunButton.style.cursor = 'pointer'
          paymentRunButton.textContent = `Herunterladen (${payments.length})`
          console.log(`Lux Inject: Found ${payments.length} payments, button enabled`)
          // Stop polling once payments are found
          if (paymentCheckInterval) {
            clearInterval(paymentCheckInterval)
            paymentCheckInterval = null
          }
        } else {
          paymentRunButton.disabled = true
          paymentRunButton.style.opacity = '0.5'
          paymentRunButton.style.cursor = 'not-allowed'
          paymentRunButton.textContent = 'Herunterladen'
        }
      }

      // Initial check and set up interval
      setTimeout(checkForPayments, 1000)
      paymentCheckInterval = setInterval(checkForPayments, 2000)

      // Clean up interval when button is removed from DOM
      const cleanupObserver = new MutationObserver(() => {
        if (!document.contains(paymentRunButton)) {
          clearInterval(paymentCheckInterval)
          cleanupObserver.disconnect()
        }
      })
      cleanupObserver.observe(document.body, { childList: true, subtree: true })

      paymentRunButton.addEventListener('click', async () => {
        console.log('Lux Inject: Payment Run Button clicked')
        
        Lux_setPaymentButtonLoading(paymentRunButton, true)
        
        try {
          // Extract payments from React state
          const payments = Lux_extractPaymentsFromState()
          
          if (!payments || payments.length === 0) {
            alert('Keine Zahlungen gefunden. Bitte stellen Sie sicher, dass die Tabelle Daten enthält.')
            Lux_setPaymentButtonLoading(paymentRunButton, false)
            return
          }
          
          console.log(`Lux Inject: Found ${payments.length} payments`)
          
          // Get auth token
          const authData = localStorage.getItem('auth')
          if (!authData) {
            throw new Error('Authentifizierungsdaten nicht gefunden')
          }
          const token = JSON.parse(authData).token
          if (!token) {
            throw new Error('Authentifizierungstoken nicht gefunden')
          }
          
          // Call our API to generate PDF
          const response = await fetch('/api/reports/payment-overview', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ payments })
          })
          
          if (!response.ok) {
            throw new Error(`HTTP-Fehler: ${response.status}`)
          }
          
          // Download the PDF
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `zahlungsuebersicht_${new Date().toISOString().split('T')[0]}.pdf`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          a.remove()
          
          console.log('Lux Inject: PDF downloaded successfully')
          
        } catch (error) {
          console.error('Lux Inject: Error generating payment overview:', error)
          alert('Fehler beim Erstellen der Zahlungsübersicht: ' + error.message)
        }
        
        Lux_setPaymentButtonLoading(paymentRunButton, false)
      })

      // Replace control element with button
      controlElement.parentNode.replaceChild(paymentRunButton, controlElement)
    }

    // Insert the cloned column after the anchor element
    anchorElement.parentNode.insertBefore(clonedCol, anchorElement.nextSibling)
  }

  // Check if element already exists
  const anchorElement = document.querySelector(anchorSelector)
  if (anchorElement) {
    injectButton(anchorElement)
    return
  }

  // Set up MutationObserver to wait for the element
  Lux_PaymentRun_observer = new MutationObserver((mutationsList, observer) => {
    const anchorElement = document.querySelector(anchorSelector)
    if (anchorElement) {
      observer.disconnect()
      Lux_PaymentRun_observer = null
      injectButton(anchorElement)
    }
  })

  Lux_PaymentRun_observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}
